import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { convertGeneralContextErrorToHookForm } from '@src/common';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { errorsSelector } from '@src/features/customers/auth/store/auth';
import { useLoginEmailField } from '../useLoginEmailField';
import { useLogin } from '../useLogin';
import { passwordLoginSchema, type PasswordLoginFormValues } from './passwordLoginSchema';

type UsePasswordLoginParams = {
  initialEmail: string;
  onEmailChange?: (email: string) => void;
  onLoggedIn?: () => void;
};

export const usePasswordLogin = (params: UsePasswordLoginParams) => {
  const { initialEmail, onEmailChange, onLoggedIn } = params;
  const { i18n } = useTranslate();
  const contextErrors = useAppSelector(errorsSelector);
  const { submitPassword, loading, clearErrors } = useLogin();
  const schema = useMemo(() => passwordLoginSchema(i18n), [i18n]);

  const { control, setValue, setError, handleSubmit, watch, formState } = useForm<PasswordLoginFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: { email: initialEmail ?? '', password: '' },
  });
  const { errors } = formState;

  const { email, onEmailInputChange } = useLoginEmailField({
    initialEmail,
    onEmailChange,
    setValue,
    watch,
  });
  const password = watch('password') ?? '';

  useEffect(() => {
    convertGeneralContextErrorToHookForm('password', setError, contextErrors, 'login');
  }, [contextErrors, setError]);

  const isEmailValid = schema.shape.email.safeParse(email).success;
  const isPasswordValid = schema.shape.password.safeParse(password).success;
  const isSubmitEnabled = isEmailValid && isPasswordValid;

  const onSubmit = handleSubmit(async (data) => {
    const loggedIn = await submitPassword(data.email, data.password);

    if (loggedIn) {
      onLoggedIn?.();
    }
  });

  return {
    control,
    errors,
    loading,
    isSubmitEnabled,
    isEmailValid,
    email,
    onSubmit,
    clearErrors,
    onEmailInputChange,
    setPassword: (value: string) => setValue('password', value, { shouldValidate: true }),
  };
};

export default usePasswordLogin;
